import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiFailure } from '../../app/errors'
import { assignProjectImport, type ProjectImportAssignmentBundle } from '../../app/project-imports'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { t } from '../../copy'

export function ProjectImportAssigneePicker({
  importId,
  draftReady = false,
  regenerate = false,
  onAssigned,
}: {
  importId: string
  draftReady?: boolean
  regenerate?: boolean
  onAssigned: (bundle: ProjectImportAssignmentBundle) => void | Promise<void>
}) {
  const [error, setError] = useState('')
  const assign = useMutation({
    mutationFn: () => assignProjectImport(importId),
  })

  async function createHandoff() {
    setError('')
    try {
      await onAssigned(await assign.mutateAsync())
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  return <Card className="mb-npc-grid max-w-[640px]" data-testid="project-import-assignee-step">
    {draftReady ? <p className="m-0 mb-npc-grid-sm text-npc-body-sm text-npc-ink-soft">{t('project_import.wizard.draft_ready')}</p> : null}
    <CardTitle>{t('project_import.assignee.one_time_title')}</CardTitle>
    <CardHint className="mt-npc-gap-sm">{t('project_import.assignee.one_time_hint')}</CardHint>
    {error ? <p className="m-0 mt-npc-grid-sm text-npc-body-sm text-npc-red-ink" data-testid="project-import-error">{error}</p> : null}
    <div className="mt-npc-grid-sm">
      <Button type="button" variant="primary" disabled={assign.isPending} onClick={() => void createHandoff()} data-testid="project-import-continue">
        {assign.isPending
          ? t('project_import.assignee.assigning')
          : t(regenerate ? 'project_import.assignee.regenerate_one_time' : 'project_import.assignee.create_one_time')}
      </Button>
    </div>
  </Card>
}
